import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, errMsg } from "@/lib/api";
import { EmptyState } from "@/components/app/EmptyState";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Loader2, Plus, Pencil, Trash2, UserCog, Search, ShieldCheck,
  Tractor, User as UserIcon, Users,
} from "lucide-react";
import { toast } from "sonner";

const LEER = {
  email: "", name: "", rolle: "lieferant", password: "",
  liefernummern: "", aktiv: true,
};

// Rollen-Mapping inkl. sysadmin
const ROLLEN = {
  sysadmin: { label: "System-Administrator", short: "SysAdmin", staff: true, badge: "default" },
  admin: { label: "Molkerei (Admin)", short: "Admin", staff: true, badge: "secondary" },
  berater: { label: "HofBerater", short: "Berater", staff: true, badge: "secondary" },
  lieferant: { label: "Lieferant", short: "Lieferant", staff: false, badge: "outline" },
};

const rolleInfo = (r) => ROLLEN[r] || { label: r, short: r, staff: false, badge: "outline" };

const StatusBadge = ({ aktiv }) =>
  aktiv ? (
    <Badge variant="outline" className="text-[hsl(var(--success))] border-[hsl(var(--success))]">aktiv</Badge>
  ) : (
    <Badge variant="outline">inaktiv</Badge>
  );

/** Kompakte Karten-Darstellung eines Benutzers (Mobile-View). */
const BenutzerCard = ({ r, onEdit, onDelete }) => {
  const info = rolleInfo(r.rolle);
  return (
    <div className="p-3 space-y-2" data-testid={`user-card-${r.email}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium truncate">{r.name}</span>
            <StatusBadge aktiv={r.aktiv} />
          </div>
          <p className="text-xs text-muted-foreground truncate">{r.email}</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant={info.badge} className="text-[10px] py-0.5">{info.short}</Badge>
        {r.rolle === "lieferant" && r.liefernummern?.length > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground font-num">
            <Tractor className="h-3 w-3 shrink-0" />
            {r.liefernummern.join(", ")}
          </span>
        )}
      </div>
      <div className="flex justify-end gap-1 pt-1">
        <Button
          data-testid={`user-edit-button-${r.email}`}
          variant="ghost" size="icon" className="h-8 w-8"
          onClick={() => onEdit(r)}
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          data-testid={`user-delete-button-${r.email}`}
          variant="ghost" size="icon" className="h-8 w-8"
          onClick={() => onDelete(r)}
        >
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </div>
    </div>
  );
};

/** Kompakter Tabellen-Body + Mobile-Cards (wiederverwendbar für beide Tabs). */
const BenutzerListe = ({ rows, loading, kategorie, onEdit, onDelete }) => {
  if (loading) {
    return (
      <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
    );
  }
  if (!rows.length) {
    return (
      <EmptyState
        icon={kategorie === "staff" ? ShieldCheck : Tractor}
        title="Keine Treffer."
        text="Passen Sie den Suchbegriff oder Filter an."
      />
    );
  }
  return (
    <>
      {/* Mobile: Karten (< md) */}
      <div className="md:hidden divide-y">
        {rows.map((r) => (
          <BenutzerCard key={r.id} r={r} onEdit={onEdit} onDelete={onDelete} />
        ))}
      </div>

      {/* Desktop: Tabelle (>= md) */}
      <div className="hidden md:block overflow-x-auto">
        <Table data-testid={`users-table-${kategorie}`}>
          <TableHeader>
            <TableRow>
              <TableHead>E-Mail</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Rolle</TableHead>
              {kategorie === "lieferant" && <TableHead>Liefernummern</TableHead>}
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const info = rolleInfo(r.rolle);
              return (
                <TableRow key={r.id}>
                  <TableCell className="font-medium text-sm">{r.email}</TableCell>
                  <TableCell className="text-sm">{r.name}</TableCell>
                  <TableCell>
                    <Badge variant={info.badge}>{info.label}</Badge>
                  </TableCell>
                  {kategorie === "lieferant" && (
                    <TableCell className="font-num text-sm">
                      {r.liefernummern?.join(", ") || <span className="text-muted-foreground">–</span>}
                    </TableCell>
                  )}
                  <TableCell><StatusBadge aktiv={r.aktiv} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        data-testid={`user-edit-button-${r.email}`}
                        variant="ghost" size="icon"
                        onClick={() => onEdit(r)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        data-testid={`user-delete-button-${r.email}`}
                        variant="ghost" size="icon"
                        onClick={() => onDelete(r)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
};

export default function AdminBenutzerPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [suche, setSuche] = useState("");
  const [sucheDebounced, setSucheDebounced] = useState("");
  const [tab, setTab] = useState("staff");

  useEffect(() => {
    const t = setTimeout(() => setSucheDebounced(suche.trim()), 250);
    return () => clearTimeout(t);
  }, [suche]);

  const load = useCallback(() => {
    setLoading(true);
    const params = sucheDebounced ? { suche: sucheDebounced } : {};
    api.get("/benutzer", { params })
      .then((res) => setRows(res.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [sucheDebounced]);

  useEffect(() => { load(); }, [load]);

  // Split nach Rolle für die Tabs
  const staff = useMemo(
    () => rows.filter((r) => rolleInfo(r.rolle).staff),
    [rows]
  );
  const lieferanten = useMemo(
    () => rows.filter((r) => r.rolle === "lieferant"),
    [rows]
  );

  const speichern = async () => {
    setSaving(true);
    try {
      const nummern = (form.liefernummern || "").split(",").map((s) => s.trim()).filter(Boolean);
      if (form.id) {
        const payload = { name: form.name, rolle: form.rolle, liefernummern: nummern, aktiv: form.aktiv };
        if (form.password) payload.password = form.password;
        await api.put(`/benutzer/${form.id}`, payload);
        toast.success("Benutzer aktualisiert.");
      } else {
        await api.post("/benutzer", {
          email: form.email, name: form.name, rolle: form.rolle,
          password: form.password, liefernummern: nummern, aktiv: form.aktiv,
        });
        toast.success("Benutzer angelegt.");
      }
      setForm(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const loeschen = async (r) => {
    if (!window.confirm(`Benutzer ${r.email} wirklich löschen?`)) return;
    try {
      await api.delete(`/benutzer/${r.id}`);
      toast.success("Benutzer gelöscht.");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const oeffnenBearbeiten = (r) =>
    setForm({
      ...r,
      password: "",
      liefernummern: (r.liefernummern || []).join(", "),
    });

  const neuerBenutzer = () => setForm({
    ...LEER,
    // Wähle Default-Rolle basierend auf aktivem Tab, damit "Neuer Benutzer" im Staff-Tab
    // gleich einen Admin vorbereitet.
    rolle: tab === "staff" ? "admin" : "lieferant",
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Benutzer-Verwaltung</h1>
          <p className="text-sm text-muted-foreground">
            {staff.length} administrative · {lieferanten.length} Lieferant(en) · Änderungen wirken sofort auf Zugänge.
          </p>
        </div>
        <Button data-testid="user-create-button" onClick={neuerBenutzer}>
          <Plus className="h-4 w-4 mr-2" /> Neuer Benutzer
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] sm:flex-none">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="users-search-input"
            className="pl-8 sm:w-[320px] bg-card"
            placeholder="E-Mail, Name, Liefernr. …"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
          />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full sm:w-auto h-auto p-1">
          <TabsTrigger value="staff" data-testid="users-tab-staff" className="flex-1 sm:flex-none">
            <ShieldCheck className="h-4 w-4 sm:mr-1.5" />
            <span className="ml-1.5 sm:ml-0">Administration</span>
            <Badge variant="secondary" className="ml-2 h-4 text-[10px] px-1.5 font-num">
              {staff.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="lieferant" data-testid="users-tab-lieferant" className="flex-1 sm:flex-none">
            <Tractor className="h-4 w-4 sm:mr-1.5" />
            <span className="ml-1.5 sm:ml-0">Lieferanten</span>
            <Badge variant="secondary" className="ml-2 h-4 text-[10px] px-1.5 font-num">
              {lieferanten.length}
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="staff" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <BenutzerListe
                rows={staff}
                loading={loading}
                kategorie="staff"
                onEdit={oeffnenBearbeiten}
                onDelete={loeschen}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="lieferant" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <BenutzerListe
                rows={lieferanten}
                loading={loading}
                kategorie="lieferant"
                onEdit={oeffnenBearbeiten}
                onDelete={loeschen}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent data-testid="user-form-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>{form?.id ? "Benutzer bearbeiten" : "Neuer Benutzer"}</DialogTitle>
            <DialogDescription>
              Mehrere Liefernummern mit Beistrich trennen (z. B. 100234, 100235).
            </DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>E-Mail *</Label>
                <Input
                  data-testid="user-form-email" type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  disabled={!!form.id}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Name *</Label>
                <Input
                  data-testid="user-form-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Rolle</Label>
                  <Select value={form.rolle} onValueChange={(v) => setForm({ ...form, rolle: v })}>
                    <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(ROLLEN).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{form.id ? "Neues Passwort (optional)" : "Passwort *"}</Label>
                  <Input
                    data-testid="user-form-password" type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                  />
                </div>
              </div>
              {form.rolle === "lieferant" && (
                <div className="space-y-1.5">
                  <Label>Liefernummern</Label>
                  <Input
                    data-testid="user-form-liefernummern"
                    value={form.liefernummern}
                    onChange={(e) => setForm({ ...form, liefernummern: e.target.value })}
                    placeholder="100234, 100235"
                  />
                  <p className="text-xs text-muted-foreground">
                    Der Benutzer hat Zugriff auf alle angegebenen Liefernummern.
                  </p>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Switch checked={form.aktiv} onCheckedChange={(v) => setForm({ ...form, aktiv: v })} />
                <Label>Aktiv</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Abbrechen</Button>
            <Button
              data-testid="user-form-save-button"
              onClick={speichern}
              disabled={saving || !form?.email || !form?.name || (!form?.id && !form?.password)}
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
